import { reactive } from 'vue'
import { IMapState } from 'yandex-maps'

const SCRIPT_SRC = process.env.VUE_APP_MAP_SCRIPT_URL
const API_KEY = process.env.VUE_APP_MAP_KEY

const DEFAULT_MAP_PROPS: IMapState = {
    center: [43.25391893213578, 76.92523545812777],
    zoom: 13,
    controls: [],
}

type Coordinates = {
    latitude: number
    longitude: number
}

export default function (callback?: () => void) {
    const state = reactive({
        is_loading: false,
        is_failed: false,
        is_ready: false,
        // layers
        is_yandex_layer: true,
        is_gis_layer: false,
    })

    const createMap = (element_id: string, props: IMapState): ymaps.Map => {
        return new window.ymaps.Map(element_id, props)
    }

    const getCoordinatesFromEvent = (event: ymaps.MapEvent): Coordinates => {
        const coords = event.get('coords') as number[]

        const coordinates: Coordinates = {
            latitude: coords[0],
            longitude: coords[1],
        }

        return coordinates
    }

    const onScriptLoaded = () => {
        state.is_loading = false
        state.is_failed = false
        state.is_ready = true

        window.ymaps.ready(callback)
    }

    const isScriptLoaded = (): boolean => {
        const ymapsScript = document.head.querySelector('script#ymaps')
        return Boolean(ymapsScript)
    }

    const loadMapScript = () => {
        if (isScriptLoaded()) {
            if (callback) callback()
            return
        }

        try {
            state.is_loading = true

            const script = document.createElement('script')
            script.id = 'ymaps'
            script.src = `${SCRIPT_SRC}?apikey=${API_KEY}&lang=ru_RU`

            script.onload = onScriptLoaded

            document.head.append(script)
        } catch (error) {
            state.is_loading = false
            state.is_failed = true

            console.error('Map script error', error)
        }
    }

    const setMapCenter = (map: ymaps.Map, coordinates: number[]) => {
        map.panTo(coordinates)
    }

    const getPolygonCenter = (coordinates: number[][]) => {
        let sumLat = 0
        let sumLng = 0

        for (const coord of coordinates) {
            sumLat += coord[0]
            sumLng += coord[1]
        }

        const avgLat = sumLat / coordinates.length
        const avgLng = sumLng / coordinates.length

        return [avgLat, avgLng]
    }

    const addGisLayer = () => {
        const gis_layer = function () {
            // @ts-ignore
            return new window.ymaps.Layer('http://tile2.maps.2gis.com/tiles?x=%x&y=%y&z=%z', {
                notFoundTile: ''.concat('//tile%d|2.maps.2gis.com/tiles', '/2/0-0.jpeg'),
                // @ts-ignore
                projection: window.ymaps.projection.sphericalMercator,
            })
        }

        // @ts-ignore
        window.ymaps.layer.storage.add('2gis#layer', gis_layer)
        // @ts-ignore
        window.ymaps.mapType.storage.add(
            '2gis#map',
            new window.ymaps.MapType('2gis#map', ['2gis#layer'])
        )
    }

    const clearMap = (map: ymaps.Map) => {
        map.geoObjects.removeAll()
    }

    const createPlacemark = (coordinates: number[]) => {
        const placemark = new window.ymaps.Placemark(
            coordinates,
            {},
            {
                preset: 'islands#blueDotIcon',
            }
        )

        return placemark
    }

    const addGeoObject = (map: ymaps.Map, geo_object: ymaps.GeoObject) => {
        map.geoObjects.add(geo_object)
    }

    const changeLayer = (map: ymaps.Map, layer_key: string) => {
        if (layer_key === 'yandex#map') {
            state.is_yandex_layer = true
            state.is_gis_layer = false
        }

        if (layer_key === '2gis#map') {
            state.is_yandex_layer = false
            state.is_gis_layer = true
        }

        map.setType(layer_key)
    }

    const removeLastAddedGeoObject = (map: ymaps.Map) => {
        const object_index = map.geoObjects.getLength()
        map.geoObjects.splice(object_index - 1, 1)
    }

    const getLastAddedGeoObjectIndex = (map: ymaps.Map): number => {
        return map.geoObjects.getLength() - 1
    }

    const createBalloon = (map: ymaps.Map, polygon: ymaps.Polygon, text: string) => {
        if (!polygon.geometry) return

        const myPlacemark = new window.ymaps.Placemark(
            getPolygonCenter(polygon.geometry.getCoordinates()[0]),
            {
                iconContent: `Сектор ${text}`,
            },
            {
                preset: 'islands#blackStretchyIcon',
            }
        )

        // Добавляем метку на карту
        map.geoObjects.add(myPlacemark)
    }

    return {
        state,
        loadMapScript,
        isScriptLoaded,
        createMap,
        getCoordinatesFromEvent,
        setMapCenter,
        clearMap,
        createPlacemark,
        addGeoObject,
        addGisLayer,
        changeLayer,
        removeLastAddedGeoObject,
        getLastAddedGeoObjectIndex,
        createBalloon,
    }
}
